import { useState, useEffect, useCallback, useMemo, memo } from "react"
import { DayCell } from "./DayCell"
import { Button } from "../ui/Button"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { format } from "date-fns"
import { useQuery } from "convex/react"
import { api } from "../../../convex/_generated/api"
import type { Id } from "../../../convex/_generated/dataModel"
import { useCalendar } from "../../hooks/useCalendar"
import { logger } from "../../utils/logger"
import { cn } from "../../utils/cn"
import { useEventsStore, mapConvexEvent } from "../../stores/eventsStore"

interface CalendarProps {
  view: "care" // only care grid
  selectedChild: string
  selectedDate?: Date | null
  onDayClick: (date: Date) => void
  onDayDoubleClick?: (date: Date) => void
}

export const Calendar = memo(function Calendar({ selectedChild, selectedDate, onDayClick, onDayDoubleClick }: CalendarProps) {
  const {
    currentDate,
    monthName,
    nextMonth,
    prevMonth
  } = useCalendar()
  const { setEvents } = useEventsStore()

  const [dragStart, setDragStart] = useState<Date | null>(null)
  const [dragEnd, setDragEnd] = useState<Date | null>(null)
  const [isDragging, setIsDragging] = useState(false)

  // Use useMemo for today to avoid creating a new object on every render
  const today = useMemo(() => new Date(), [])

  const navigateMonth = useCallback((direction: "prev" | "next") => {
    if (direction === "prev") {
      prevMonth()
    } else {
      nextMonth()
    }
  }, [prevMonth, nextMonth])

  const getMonthDays = useCallback(() => {
    // Generate days directly from currentDate, without dependency on entries
    const year = currentDate.getFullYear()
    const month = currentDate.getMonth()
    const firstDayOfMonth = new Date(year, month, 1)
    const lastDayOfMonth = new Date(year, month + 1, 0)
    const firstDayWeekday = firstDayOfMonth.getDay()
    const adjustedFirstDayWeekday = firstDayWeekday === 0 ? 6 : firstDayWeekday - 1
    const firstDisplayDay = new Date(firstDayOfMonth)
    firstDisplayDay.setDate(firstDayOfMonth.getDate() - adjustedFirstDayWeekday)
    const lastDisplayDay = new Date(lastDayOfMonth)
    const lastDayWeekday = lastDayOfMonth.getDay()
    const adjustedLastDayWeekday = lastDayWeekday === 0 ? 6 : lastDayWeekday - 1
    const remainingDays = 6 - adjustedLastDayWeekday
    lastDisplayDay.setDate(lastDayOfMonth.getDate() + remainingDays)
    
    const days = []
    const currentDay = new Date(firstDisplayDay)
    while (currentDay <= lastDisplayDay) {
      days.push(new Date(currentDay))
      currentDay.setDate(currentDay.getDate() + 1)
    }
    return days
  }, [currentDate])

  const days = useMemo(() => getMonthDays(), [getMonthDays])

  // Convex reactive query for events — auto-updates when data changes
  const isValidChild = !!(selectedChild && selectedChild !== 'temp-child-id')
  const eventsStartDate = useMemo(() => {
    if (days.length === 0) return ''
    const d = new Date(days[0])
    d.setHours(0, 0, 0, 0)
    return d.toISOString()
  }, [days])
  const eventsEndDate = useMemo(() => {
    if (days.length === 0) return ''
    const d = new Date(days[days.length - 1])
    d.setHours(23, 59, 59, 999)
    return d.toISOString()
  }, [days])

  const convexEvents = useQuery(
    api.events.queries.getEventsByChild,
    isValidChild && eventsStartDate
      ? { childId: selectedChild as Id<"children">, startDate: eventsStartDate, endDate: eventsEndDate }
      : "skip"
  )

  // Sync Convex events → eventsStore for DayCell / DayEventsList compatibility
  useEffect(() => {
    if (convexEvents !== undefined) {
      setEvents(convexEvents.map(mapConvexEvent))
    }
  }, [convexEvents, setEvents])

  const handleDayClick = useCallback((date: Date) => {
    onDayClick(date)
  }, [onDayClick])

  const handleDayDoubleClick = useCallback((date: Date) => {
    if (onDayDoubleClick) onDayDoubleClick(date)
  }, [onDayDoubleClick])

  const handleMouseDown = useCallback((date: Date) => {
    setDragStart(date)
    setIsDragging(true)
  }, [])

  const handleMouseEnter = useCallback((date: Date) => {
    if (isDragging && dragStart) {
      setDragEnd(date)
    }
  }, [isDragging, dragStart])

  const handleMouseUp = useCallback(() => {
    if (dragStart && dragEnd) {
      logger.debug("Selected range:", dragStart, "to", dragEnd)
      // TODO: Implement range selection for calendar entries
    }
    setDragStart(null)
    setDragEnd(null)
    setIsDragging(false)
  }, [dragStart, dragEnd])

  useEffect(() => {
    const handleGlobalMouseUp = () => {
      if (isDragging) {
        handleMouseUp()
      }
    }

    document.addEventListener("mouseup", handleGlobalMouseUp)
    return () => document.removeEventListener("mouseup", handleGlobalMouseUp)
  }, [isDragging, handleMouseUp])

  const weekDays = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]

  // Optimized day cell rendering - DayCell gets data directly from store
  const dayCells = useMemo(() => {
    const selectedDateString = selectedDate ? format(selectedDate, 'yyyy-MM-dd') : null
    return days.map((date, index) => {
      const dateString = format(date, 'yyyy-MM-dd')
      const isCurrentMonth = currentDate.getMonth() === date.getMonth() && currentDate.getFullYear() === date.getFullYear()
      
      return (
        <DayCell
          key={`${dateString}-${index}`}
          date={date}
          isCurrentMonth={isCurrentMonth}
          isToday={date.toDateString() === today.toDateString()}
          selectedChild={selectedChild}
          isSelected={selectedDateString === dateString}
          onClick={handleDayClick}
          onDoubleClick={handleDayDoubleClick}
          onMouseDown={handleMouseDown}
          onMouseEnter={handleMouseEnter}
          isDragSelected={
            !!(dragStart &&
            dragEnd &&
            date >= new Date(Math.min(dragStart.getTime(), dragEnd.getTime())) &&
            date <= new Date(Math.max(dragStart.getTime(), dragEnd.getTime())))}
        />
      )
    })
  }, [days, currentDate, today, selectedChild, selectedDate, handleDayClick, handleMouseDown, handleMouseEnter, dragStart, dragEnd])

  return (
    <div className="card-glass overflow-hidden">
      {/* Calendar Header */}
      <div className="flex items-center justify-between p-3 sm:p-6 border-b border-white/20">
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigateMonth("prev")}
          className="glass-button"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>

        <h2 className="text-lg sm:text-xl font-bold bg-gradient-to-r from-slate-700 to-slate-900 bg-clip-text text-transparent">
          {monthName}
        </h2>

        <Button
          variant="outline"
          size="sm"
          onClick={() => navigateMonth("next")}
          className="glass-button"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {/* Calendar Grid */}
      <div className="p-1 sm:p-3 md:p-6">
        {/* Week day headers */}
        <div className="calendar-header grid grid-cols-7 gap-1 sm:gap-2 mb-1 sm:mb-4">
          {weekDays.map((day) => {
            const isWeekend = day === "Sat" || day === "Sun"
            return (
              <div
                key={day}
                className={cn(
                  "text-center text-xs sm:text-sm font-semibold py-1 sm:py-3 rounded-lg",
                  isWeekend 
                    ? "text-slate-700 bg-gradient-to-b from-slate-200 to-slate-300" 
                    : "text-slate-600 bg-gradient-to-b from-slate-50 to-slate-100"
                )}
              >
                {day}
              </div>
            )
          })}
        </div>

        {/* Calendar days with CSS Subgrid */}
        <div className="calendar-grid grid grid-cols-7 gap-1 sm:gap-3">
          {dayCells}
        </div>
      </div>
    </div>
  )
})

export default Calendar